import { useAnalyticsContext } from '../providers/AnalyticsProvider'
import { useAnalyticsReportQuery } from '../queries/useAnalyticsReportQuery'
import type { AnalyticsReportData, AnalyticsReportDefinition } from '../types'
import { getExternalSource } from '../utils/getExternalSource'
import { useAnalyticsCardLegend } from './useAnalyticsCardLegend'
import { useAnalyticsQueryParams } from './useAnalyticsQueryParams'
import { useReportLink } from './useReportLink'

type UseAnalyticsReportBaseParams = {
  reportId: string
  filters?: Record<string, string>
}

export const useAnalyticsReportBase = ({ reportId, filters }: UseAnalyticsReportBaseParams) => {
  const { isLoading: isReportDefinitionLoading, getReportDefinition } = useAnalyticsContext()
  const reportDefinition = getReportDefinition(reportId)
  const supportedPrecisions = reportDefinition?.availableAggregationPrecisions
  const analyticsQueryParams = useAnalyticsQueryParams()
  const supportsPrecisions = supportedPrecisions && supportedPrecisions.length > 0
  const isSelectedPrecisionSupported =
    !supportsPrecisions || supportedPrecisions?.includes(analyticsQueryParams.aggregationPrecision)

  const queryParams = supportsPrecisions
    ? analyticsQueryParams
    : (() => {
        const { aggregationPrecision, ...paramsWithoutPrecision } = analyticsQueryParams

        return paramsWithoutPrecision
      })()

  const { analyticsReportQuery } = useAnalyticsReportQuery(
    [{ id: reportId }, { ...queryParams, ...filters }],
    {
      enabled: !!reportDefinition && isSelectedPrecisionSupported,
      staleTime: 30 * 60 * 1000, // 30 minutes - data is fresh for 30 minutes
      cacheTime: 2 * 60 * 60 * 1000, // 2 hours - keep in cache for 2 hours
      refetchOnWindowFocus: false, // Don't refetch on window focus for analytics
      refetchOnMount: false, // Don't refetch if data is fresh
      refetchInterval: 60 * 60 * 1000, // Auto-refetch every hour
      refetchIntervalInBackground: false, // Don't refetch in background tabs
    },
  )

  const reportData = (
    isSelectedPrecisionSupported ? analyticsReportQuery?.data : { report: { rows: [] } }
  ) as AnalyticsReportData
  const reportLink = useReportLink({
    reportId,
    filters,
  })
  const legend = useAnalyticsCardLegend()

  const activeColumn = reportDefinition?.columns.find(
    (column) => column.key === reportDefinition?.yAxisDefaultValue,
  ) as AnalyticsReportDefinition['columns'][0] | undefined

  const emptyMessage = !isSelectedPrecisionSupported
    ? `This report does not support grouping by ${analyticsQueryParams.aggregationPrecision}`
    : 'No data found in this date range'

  return {
    analyticsReportQuery,
    reportDefinition,
    reportData,
    reportLink,
    legend,
    activeColumn,
    emptyMessage,
    externalSource: getExternalSource(reportDefinition?.dataSource),
    isReportDefinitionLoading,
    isReportDataLoading: isSelectedPrecisionSupported ? analyticsReportQuery.isLoading : false,
    supportsPrecisions,
    supportedPrecisions,
    isSelectedPrecisionSupported,
  }
}
